import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  InvoicingSchedulingOptions,
  InvoicingServiceMode,
  InvoicingPaymentMethods,
} from "@/types/invoicing";

interface Service {
  id: number;
  name: string;
  price: number;
  description: string;
  mode: InvoicingServiceMode;
}

interface PaymentMethod {
  id: number;
  method: InvoicingPaymentMethods;
  description?: string;
  is_default: boolean;
}

export interface InvoicingProfile {
  id?: string;
  therapist_id?: string;
  name?: string;
  id_number?: string;
  country?: string;
  city?: string;
  address?: string;
  zip_code?: string;
  schedule?: InvoicingSchedulingOptions;
  services?: Service[];
  payment_methods?: PaymentMethod[];
  created_at?: string;
  updated_at?: string;
}

export interface PatientInvoicingPreferences {
  id: string;
  therapist_id: string;
  patient_id: string;
  schedule: InvoicingSchedulingOptions;
  payment_method: InvoicingPaymentMethods;
  service_id: string;
  service?: Service;
  created_at: string;
  updated_at: string;
}

export interface PatientInvoicingPreferencesInput {
  schedule: InvoicingSchedulingOptions;
  payment_method: InvoicingPaymentMethods;
  service_id: string;
}

export const invoicingProfileApi = createApi({
  reducerPath: "invoicingProfileApi",
  baseQuery: fetchBaseQuery({ baseUrl: "/api" }),
  tagTypes: ["InvoicingProfile", "PatientInvoicingPreferences"],
  endpoints: (builder) => ({
    getInvoicingProfile: builder.query<InvoicingProfile | null, void>({
      query: () => "/invoicing/profile",
      providesTags: (result, error, arg) =>
        result
          ? [{ type: "InvoicingProfile", id: result.id || "CURRENT" }]
          : ["InvoicingProfile"],
    }),
    updateInvoicingProfile: builder.mutation<
      InvoicingProfile,
      Partial<InvoicingProfile>
    >({
      query: (profileData) => ({
        url: "/invoicing/profile",
        method: "PUT",
        body: profileData,
      }),
      invalidatesTags: (result, error, arg) =>
        result
          ? [{ type: "InvoicingProfile", id: result.id || "CURRENT" }]
          : ["InvoicingProfile"],
    }),
    getPatientInvoicingPreferences: builder.query<
      PatientInvoicingPreferences | null,
      string
    >({
      query: (patientId) => `/patient/${patientId}/invoicing`,
      providesTags: (result, error, patientId) =>
        result
          ? [{ type: "PatientInvoicingPreferences", id: patientId }]
          : ["PatientInvoicingPreferences"],
    }),
    updatePatientInvoicingPreferences: builder.mutation<
      PatientInvoicingPreferences,
      { patientId: string; data: PatientInvoicingPreferencesInput }
    >({
      query: ({ patientId, data }) => ({
        url: `/patient/${patientId}/invoicing`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, error, { patientId }) => [
        { type: "PatientInvoicingPreferences", id: patientId },
      ],
    }),
  }),
});

export const {
  useGetInvoicingProfileQuery,
  useUpdateInvoicingProfileMutation,
  useGetPatientInvoicingPreferencesQuery,
  useUpdatePatientInvoicingPreferencesMutation,
} = invoicingProfileApi;
